import React, { useMemo, useState, useEffect } from 'react'
import { router } from '@inertiajs/react'
import {
  Dialog,
  DialogBody,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { Button } from '@/components/ui/button'
import { Plus, Loader2, Info, ArrowLeft, ArrowRight } from 'lucide-react'
import { partitionCandidates, type PropertyGroupRow } from '@/lib/groups'
import { mappingPayload, parseMappingErrors, type MappingErrorItem, type MappingRequest } from '@/lib/combined-inventory'
import { useRoomMapping } from '@/hooks/useRoomMappingPreview'
import RoomMappingEditor from '@/components/account/groups/RoomMappingEditor'
import { cn } from '@/lib/utils'

interface AddHotelDialogProps {
  /**
   * `/a/:account_slug/settings` — the business's settings root, served by the backend
   * (`currentAccount.settingsPath`). Every endpoint below is this plus a FIXED suffix.
   */
  settingsPath: string
  group: PropertyGroupRow | null
  onClose: () => void
}

// ADDING ONE MORE FOLLOWING LISTING TO AN EXISTING COMBINED INVENTORY (a PropertyGroup in code).
//
// Only the account's UNGROUPED hotels are candidates, and each arrives already classified by the
// backend: a hotel on another clock or in another currency is listed with the reason rather than
// hidden, because "why isn't my hotel in this list?" is a support ticket and "prices in EUR, but this
// group prices in GBP" is an answer. A hotel already in another group never appears at all — it is
// not ungrouped, and the mutation refuses it anyway.
//
// One hotel at a time, deliberately: joining changes which configuration prices the hotel and pools
// its occupancy into the group's demand, and a batch attach would report one outcome for several
// hotels that may each fail differently.
//
// COMBINED INVENTORY. Picking the hotel is step one; step two maps its rooms onto the lead listing's
// (RoomMappingEditor, drawn by the server). It joins only once every room is a Same room or an Added
// room, and the membership and the mapping are saved as one act.
export default function AddHotelDialog({ settingsPath, group, onClose }: AddHotelDialogProps) {
  const [selectedId, setSelectedId] = useState<number | null>(null)
  const [submitting, setSubmitting] = useState(false)
  const [step, setStep] = useState<'pick' | 'mapping'>('pick')
  const [saveErrors, setSaveErrors] = useState<MappingErrorItem[] | null>(null)

  useEffect(() => {
    setSelectedId(null)
    setSubmitting(false)
    setStep('pick')
    setSaveErrors(null)
  }, [group?.id])

  const { eligible, blocked } = useMemo(
    () => partitionCandidates(group?.candidates ?? [], null),
    [group?.candidates],
  )

  const mappingRequest = useMemo<MappingRequest | null>(
    () => (group && selectedId !== null ? { mode: 'join', groupId: group.id, propertyIds: [selectedId] } : null),
    [group, selectedId],
  )
  const mapping = useRoomMapping(settingsPath, mappingRequest)
  const selected = eligible.find((candidate) => candidate.id === selectedId) ?? null

  function submit() {
    if (!group || selectedId === null || !mapping.canSave || !mapping.decisions) return
    setSubmitting(true)
    setSaveErrors(null)
    router.post(
      `${settingsPath}/groups/${group.id}/members`,
      { property_id: selectedId, room_mappings: mappingPayload(mapping.decisions) },
      {
        onFinish: () => setSubmitting(false),
        onSuccess: onClose,
        onError: (errors) =>
          setSaveErrors(
            parseMappingErrors((errors as Record<string, unknown>).roomMapping) ?? [
              { roomTypeId: null, message: 'The listing could not be added. Try again.' },
            ],
          ),
        preserveScroll: true,
      },
    )
  }

  if (step === 'mapping' && group && selected) {
    return (
      <Dialog open={group !== null} onOpenChange={(next) => !next && onClose()}>
        <DialogContent className="sm:max-w-[760px]">
          <DialogHeader>
            <DialogTitle>Map the rooms at {selected.name}</DialogTitle>
            <DialogDescription>
              Each room is matched to the same room at {group.leadPropertyName}, or is an Added room it does not have.
              Check the matches and add.
            </DialogDescription>
          </DialogHeader>

          <DialogBody>
            <RoomMappingEditor
              preview={mapping.preview}
              decisions={mapping.decisions}
              onChange={mapping.setDecisions}
              loading={mapping.loading}
              error={mapping.error}
              saveErrors={saveErrors}
            />
          </DialogBody>

          <DialogFooter className="sm:items-center">
            <Button type="button" variant="ghost" className="rounded-lg" onClick={() => setStep('pick')} disabled={submitting}>
              <ArrowLeft className="mr-1.5 h-3.5 w-3.5" />
              Back
            </Button>
            <Button
              type="button"
              className="rounded-lg"
              onClick={submit}
              disabled={submitting || !mapping.canSave}
              aria-busy={submitting}
            >
              {submitting ? (
                <>
                  <Loader2 className="mr-1.5 h-3.5 w-3.5 animate-spin" />
                  Adding…
                </>
              ) : (
                <>
                  <Plus className="mr-1.5 h-3.5 w-3.5" />
                  Add listing
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    )
  }

  return (
    <Dialog open={group !== null} onOpenChange={(next) => !next && onClose()}>
      <DialogContent className="sm:max-w-[480px]">
        <DialogHeader>
          <DialogTitle>Add a following listing to {group?.name}</DialogTitle>
          <DialogDescription>
            It will be priced from {group?.leadPropertyName}&apos;s settings, and its bookings will count toward the
            building. Its own settings are kept, so removing it later restores them.
          </DialogDescription>
        </DialogHeader>

        <DialogBody className="space-y-4">
          {eligible.length === 0 && blocked.length === 0 ? (
            <div className="rounded-xl border border-dashed border-border bg-white p-10 text-center shadow-sm">
              <Plus className="mx-auto h-8 w-8 text-muted-foreground/40" />
              <p className="mt-3 text-sm font-medium text-foreground">No listings available</p>
              <p className="mt-1 text-xs text-muted-foreground">
                Every listing in this business is already in a combined inventory. Connect another property to add one.
              </p>
            </div>
          ) : (
            <div className="divide-y divide-border/50 overflow-hidden rounded-lg border border-border/60">
              {eligible.map((candidate) => (
                <button
                  key={candidate.id}
                  type="button"
                  onClick={() => setSelectedId(candidate.id)}
                  aria-pressed={selectedId === candidate.id}
                  className={cn(
                    'flex w-full cursor-pointer items-center justify-between gap-3 px-3.5 py-2.5 text-left transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-primary/40',
                    selectedId === candidate.id ? 'bg-primary/5' : 'hover:bg-muted/40',
                  )}
                >
                  <span className="min-w-0">
                    <span
                      className={cn(
                        'block truncate text-sm',
                        selectedId === candidate.id ? 'font-semibold text-primary' : 'font-medium text-foreground',
                      )}
                    >
                      {candidate.name}
                    </span>
                    <span className="block text-xs text-muted-foreground">
                      {candidate.currency} · {candidate.timezone}
                    </span>
                  </span>
                  <span
                    className={cn(
                      'h-4 w-4 shrink-0 rounded-full border',
                      selectedId === candidate.id ? 'border-primary bg-primary' : 'border-border',
                    )}
                    aria-hidden="true"
                  />
                </button>
              ))}
              {blocked.map(({ candidate, reason }) => (
                <div key={candidate.id} className="bg-muted/30 px-3.5 py-2.5">
                  <p className="truncate text-sm font-medium text-muted-foreground">{candidate.name}</p>
                  <p className="mt-0.5 flex items-start gap-1.5 text-xs text-muted-foreground">
                    <Info className="mt-0.5 h-3 w-3 shrink-0" />
                    <span>{reason}</span>
                  </p>
                </div>
              ))}
            </div>
          )}
        </DialogBody>

        <DialogFooter>
          <Button type="button" variant="ghost" className="rounded-lg" onClick={onClose} disabled={submitting}>
            Cancel
          </Button>
          <Button
            type="button"
            className="rounded-lg"
            onClick={() => setStep('mapping')}
            disabled={selectedId === null}
          >
            Next: map the rooms
            <ArrowRight className="ml-1.5 h-3.5 w-3.5" />
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
